import { type ComputedRef, type Ref, ref, computed } from "vue";
import axios from "axios";

import apiGraph, { graphGet } from "@/helpers/api/apiGraph";
import apiSkobo from "@/helpers/api/apiSkobo";

import DriveItem from "@/models/Documents/DriveItem";
import DriveItemHelper from "@/models/helpers/DriveItemHelper";
import Permission from "@/models/Documents/Permission";

import FileHelper from "@/helpers/utilities/FileHelper";

import { useFeaturesStore } from "@/stores/features";
import { useGlobalsStore } from "@/stores/globals";
import { useInstitutionsStore } from "@/stores/institutions";
import { useLogsStore } from "@/stores/logs";
import { useMeStore } from "@/stores/me";
import { usePeopleStore } from "@/stores/people";
import { useSectionsStore } from "@/stores/sections";
import { useSettingsStore } from "@/stores/settings";

interface JournalLockEntry {
  driveId: string;
  itemId: string;
  type: "Journalize" | "Lock" | string;
  status?: string;
}

interface SharedDriveLike {
  driveId: string;
  itemId: string;
  displayName?: string;
  canCopyFromRoleRestriction?: string[];
  webUrl?: string;
  [key: string]: unknown;
}

type ControlledAreaKeysShape = { system: string; entity: string; group: string } | null;

interface DocumentFunctionsComposableDependencies {
  // driveItems
  driveItem: ComputedRef<(driveId: string, itemId: string) => DriveItem | null>;
  driveItemChildren: ComputedRef<(driveId: string, itemId: string) => DriveItem[]>;
  parentDriveItem: ComputedRef<(driveId: string, itemId: string) => DriveItem | null>;
  loadDriveItem: (
    driveId: string,
    itemId: string,
    additionalProperties?: Record<string, unknown>
  ) => Promise<DriveItem>;
  reloadDriveItem: (
    driveId: string,
    itemId: string,
    additionalProperties?: Record<string, unknown>
  ) => Promise<DriveItem>;

  // myDrive
  myDriveRootId: ComputedRef<string | null>;
  calculateDriveIdOrMyDrive: ComputedRef<(driveId: string | null | undefined) => string | null | undefined>;

  // permissions
  reloadDocumentPermissions: (driveId: string, itemId: string) => Promise<Permission>;

  // sharedDrives
  sharedDrives: ComputedRef<SharedDriveLike[]>;
  myItemPermission: ComputedRef<(driveId: string, itemId: string) => Permission | null>;

  // controlledArea
  controlledAreaKeysFromDriveIdAndItemId: ComputedRef<(driveId: string, itemId: string) => ControlledAreaKeysShape>;
  controlledAreaConfidentialId: ComputedRef<
    (system: string, entity: string, group: string) => string | null | undefined
  >;

  // downloadProgress
  downloadProgressState: Ref<Record<string, number | null>>;

  // Lazy lookups for symbols still in index.ts
  getCalculateRootOrItemId: () => (driveId: string, itemId: string) => string;
  getReload: () => (driveId: string, itemId: string) => Promise<DriveItem>;
}

const createUploadSession = async (
  destinationDriveId: string,
  destinationItemId: string,
  filename: string,
  replaceItemOnConflict: boolean
) => {
  return apiGraph.post(
    `/drives/${destinationDriveId}/items/${DriveItemHelper.getItemId(
      destinationItemId
    )}:/${filename}:/createUploadSession`,
    {
      item: {
        "@microsoft.graph.conflictBehavior": replaceItemOnConflict ? "replace" : "rename"
      }
    }
  );
};

const splitFileToChuncks = (file: ArrayBuffer, chunkSize: number) => {
  let startPointer = 0;
  const endPointer = file.byteLength;
  const chunks: ArrayBuffer[] = [];
  while (startPointer < endPointer) {
    const newStartPointer = startPointer + chunkSize;
    chunks.push(file.slice(startPointer, newStartPointer));
    startPointer = newStartPointer;
  }
  return chunks;
};

const uploadFileSizeSplitLimit = 5242880;

export const useDocumentFunctionsComposable = (dependencies: DocumentFunctionsComposableDependencies) => {
  // -------------------- state --------------------
  const journalizedAndLockedDocuments: Ref<Record<string, JournalLockEntry[]>> = ref({});
  const confidentialDocumentsSharedToAulaForUserDriveMap: Ref<
    Record<string, Record<string, Array<{ itemId: string; driveId: string; [key: string]: unknown }>>>
  > = ref({});
  const confidentialDocumentsJournalizedPerUserMap: Ref<Record<string, Record<string, JournalLockEntry[]>>> = ref({});
  const confidentialDocumentsJournalizedPerSectionMap: Ref<Record<string, Record<string, JournalLockEntry[]>>> = ref(
    {}
  );
  const hiddenSharedFilesState: Ref<Array<{ driveId: string; itemId: string }>> = ref([]);
  const journalizingAndLockingDocumentsPerPersonAndDriveMap: Ref<Record<string, Record<string, JournalLockEntry[]>>> =
    ref({});
  const journalizingAndLockingDocumentsPerSectionAndDriveMap: Ref<Record<string, Record<string, JournalLockEntry[]>>> =
    ref({});
  // legacy / referenced state that isn't initialised in original code
  const appConfidentialId: Ref<string | undefined> = ref(undefined);

  // -------------------- getters --------------------
  const isDocumentJournalizing = computed(() => (appConfId: string, driveId: string, itemId: string) => {
    if (journalizedAndLockedDocuments.value[appConfId]) {
      return journalizedAndLockedDocuments.value[appConfId].some(
        (doc: JournalLockEntry) => doc.itemId === itemId && doc.driveId === driveId && doc.type === "Journalize"
      );
    }
  });

  const isDocumentLocking = computed(() => (appConfId: string, driveId: string, itemId: string) => {
    if (journalizedAndLockedDocuments.value[appConfId]) {
      return journalizedAndLockedDocuments.value[appConfId].some(
        (doc: JournalLockEntry) => doc.itemId === itemId && doc.driveId === driveId && doc.type === "Lock"
      );
    }
  });

  const confidentialDocumentsSharedToAulaForUserIdOnDriveId = computed(() => (userId: string, driveId: string) => {
    return confidentialDocumentsSharedToAulaForUserDriveMap.value[userId]
      ? confidentialDocumentsSharedToAulaForUserDriveMap.value[userId][driveId]
      : [];
  });

  const isConfidentialDocumentShareToAulaForUserIdOnDriveId = computed(
    () => (userId: string, driveId: string, itemId: string) => {
      const shares = confidentialDocumentsSharedToAulaForUserDriveMap.value[userId]
        ? confidentialDocumentsSharedToAulaForUserDriveMap.value[userId][driveId]
        : [];
      if (shares) {
        return shares.some((share: { itemId: string }) => share.itemId === itemId);
      }
      return false;
    }
  );

  const journalizedConfidentialDocumentsForUserOnDrive = computed(
    () => (userId: string, driveId: string) =>
      confidentialDocumentsJournalizedPerUserMap.value[userId]
        ? confidentialDocumentsJournalizedPerUserMap.value[userId][driveId]
        : []
  );

  const journalizedConfidentialDocumentsForSectionOnDrive = computed(
    () => (sectionId: string, driveId: string) =>
      confidentialDocumentsJournalizedPerSectionMap.value[sectionId]
        ? confidentialDocumentsJournalizedPerSectionMap.value[sectionId][driveId]
        : []
  );

  const hiddenSharedFiles = computed(() => hiddenSharedFilesState.value);

  const drivesICanCopyItemsTo = computed(() => {
    const meStore = useMeStore();
    const settingsStore = useSettingsStore();

    interface CopyDriveGroup {
      parentReference: { driveId: string | null };
      name: string;
      additionalData?: unknown;
      id?: string;
    }

    const driveGroups: CopyDriveGroup[] = [];
    driveGroups.push({
      parentReference: { driveId: dependencies.myDriveRootId.value },
      name: "OneDrive"
    });

    if (settingsStore.copyDocumentsToSectionsTeacher && meStore.isTeacher) {
      try {
        const sectionsStore = useSectionsStore();
        const sectionDrives = (
          sectionsStore.allSectionsDrives as Array<{ driveId: string; name: string; additionalData?: unknown }>
        ).map(drive => ({
          parentReference: { driveId: drive.driveId },
          name: drive.name,
          additionalData: drive.additionalData
        }));
        driveGroups.push(
          ...sectionDrives.sort((first, second) =>
            first.name.localeCompare(second.name, settingsStore.language as string | undefined)
          )
        );
        // eslint-disable-next-line no-empty
      } catch {}
    }

    try {
      const filtered = dependencies.sharedDrives.value.filter((sharePointFolder: SharedDriveLike) => {
        if (sharePointFolder.canCopyFromRoleRestriction) {
          if (meStore.isStudent && sharePointFolder.canCopyFromRoleRestriction.indexOf("student") > -1) return true;
          if (meStore.isTeacher && sharePointFolder.canCopyFromRoleRestriction.indexOf("teacher") > -1) return true;
          if (meStore.isSuperuser && sharePointFolder.canCopyFromRoleRestriction.indexOf("superuser") > -1) return true;
        }
        return false;
      });

      driveGroups.push(
        ...filtered
          .filter(
            (sharePointFolder: SharedDriveLike) =>
              dependencies.myItemPermission.value(sharePointFolder.driveId, sharePointFolder.itemId) &&
              (dependencies.myItemPermission.value(sharePointFolder.driveId, sharePointFolder.itemId)!.myPermission ===
                Permission.nctPermission.WRITE ||
                dependencies.myItemPermission.value(sharePointFolder.driveId, sharePointFolder.itemId)!.myPermission ===
                  Permission.nctPermission.OWNER)
          )
          .sort((first: SharedDriveLike, second: SharedDriveLike) =>
            (first.displayName ?? "").localeCompare(
              second.displayName ?? "",
              settingsStore.language as string | undefined
            )
          )
          .map((entry: SharedDriveLike) => ({
            name: entry.displayName ?? "",
            id: entry.itemId,
            parentReference: { driveId: entry.driveId }
          }))
      );
      // eslint-disable-next-line no-empty
    } catch {}
    return driveGroups;
  });

  // -------------------- actions --------------------
  type CopyableItem = DriveItem & { newName?: string };

  const logActivity = (
    driveId: string,
    itemId: string | null,
    parentItemId: string | null,
    action: string,
    metadata?: Record<string, unknown>
  ) => {
    let keys: ControlledAreaKeysShape | null = null;
    try {
      keys = dependencies.controlledAreaKeysFromDriveIdAndItemId.value(driveId, itemId as string);
    } catch {
      let parentItem: DriveItem | null = null;
      try {
        if (parentItemId) {
          keys = dependencies.controlledAreaKeysFromDriveIdAndItemId.value(driveId, parentItemId);
        } else {
          parentItem = dependencies.parentDriveItem.value(driveId, itemId as string);
        }

        if (parentItem) {
          keys = dependencies.controlledAreaKeysFromDriveIdAndItemId.value(driveId, parentItem.id);
        }
        // eslint-disable-next-line no-empty
      } catch {}
    }

    if (keys) {
      try {
        const confidentialId = dependencies.controlledAreaConfidentialId.value(keys.system, keys.entity, keys.group);
        if (confidentialId) {
          const logsStore = useLogsStore();
          logsStore.logActivity(confidentialId, keys?.system, keys?.entity, driveId, itemId, action, metadata);
        }
        // eslint-disable-next-line no-empty
      } catch {}
    }
  };

  const deleteDriveItems = async (itemsToDelete: DriveItem[]) => {
    const parentDriveId = itemsToDelete[0].parentReference.driveId;
    const parentItemId = itemsToDelete[0].parentReference.id;
    const promises: Array<Promise<unknown>> = [];
    itemsToDelete.forEach((item: DriveItem) => {
      promises.push(apiGraph.delete(`/drives/${item.parentReference.driveId}/items/${item.id}`));
      promises.push(apiSkobo.delete(`/AppDriveItems/${item.parentReference.driveId}/${item.id}`));
    });
    await Promise.all(promises);

    await dependencies.reloadDriveItem(parentDriveId, parentItemId);

    itemsToDelete.forEach((item: DriveItem) => {
      logActivity(item.parentReference.driveId, item.id, item.parentReference.id, "Delete", { name: item.name });
    });
  };

  const doCopyDriveItems = async (
    itemsToCopy: CopyableItem[],
    destinationDriveId: string,
    destinationItemId: string,
    cloneVersionInsteadOfCopy = false,
    replaceItemOnConflict = false
  ): Promise<Array<{ driveId: string; itemId: string }>> => {
    const promises = await itemsToCopy.map(async (item: CopyableItem) => {
      try {
        const response = await apiGraph.post(`/drives/${item.parentReference.driveId}/items/${item.id}/copy`, {
          parentReference: {
            driveId: destinationDriveId,
            id: DriveItemHelper.getItemId(destinationItemId)
          },
          name: item.newName || item.name
        });

        if (response.data.driveId && response.data.id) {
          return { driveId: response.data.driveId, itemId: response.data.id };
        } else {
          while (true) {
            const completeResponse = await axios.get(response.headers.location);
            if (
              completeResponse &&
              completeResponse.data.percentageComplete === 100 &&
              completeResponse.data.status === "completed"
            ) {
              await dependencies.reloadDriveItem(destinationDriveId, destinationItemId);
              return { driveId: destinationDriveId, itemId: completeResponse.data.resourceId };
            } else if (
              completeResponse.data.error ||
              (completeResponse.data.percentageComplete !== 100 && completeResponse.data.status === "completed")
            ) {
              if (completeResponse.data.error.code === "nameAlreadyExists") {
                if (replaceItemOnConflict) {
                  await dependencies.loadDriveItem(destinationDriveId, destinationItemId);
                  const destinationChildren = dependencies.driveItemChildren.value(
                    destinationDriveId,
                    destinationItemId
                  );
                  const conflictingItem = destinationChildren.find((child: DriveItem) => child.name === item.name);
                  if (conflictingItem) {
                    try {
                      await deleteDriveItems([conflictingItem]);
                      // eslint-disable-next-line no-empty
                    } catch {}
                    return await copyDriveItems(
                      [item],
                      destinationDriveId,
                      destinationItemId,
                      cloneVersionInsteadOfCopy,
                      replaceItemOnConflict
                    );
                  }
                } else {
                  const newItem = JSON.parse(JSON.stringify(item));
                  if (cloneVersionInsteadOfCopy) {
                    newItem.name = DriveItemHelper.calcNextVersionName(newItem.name);
                  } else {
                    newItem.name = DriveItemHelper.calcNewCopyName(newItem.name);
                  }
                  return await copyDriveItems(
                    [newItem],
                    destinationDriveId,
                    destinationItemId,
                    cloneVersionInsteadOfCopy,
                    replaceItemOnConflict
                  );
                }
              }
            }
          }
        }
      } catch (caughtError: unknown) {
        const errorWithResponse = caughtError as {
          response?: { data?: { error?: { code?: string } } | string };
          message?: string;
        };
        if (
          errorWithResponse.response?.data &&
          ((typeof errorWithResponse.response.data === "object" &&
            errorWithResponse.response.data.error?.code === "nameAlreadyExists") ||
            errorWithResponse.response.data == "Name already exists")
        ) {
          const newItem = JSON.parse(JSON.stringify(item));
          if (cloneVersionInsteadOfCopy) {
            newItem.name = DriveItemHelper.calcNextVersionName(newItem.name);
          } else {
            newItem.name = DriveItemHelper.calcNewCopyName(newItem.name);
          }
          const result = await copyDriveItems(
            [newItem],
            destinationDriveId,
            destinationItemId,
            cloneVersionInsteadOfCopy,
            replaceItemOnConflict
          );
          return result;
        } else {
          throw Error(typeof caughtError === "string" ? caughtError : errorWithResponse.message || "Copy failed");
        }
      }
    });

    const results = (await Promise.all(promises))
      .flat()
      .filter((result): result is { driveId: string; itemId: string } => !!result);
    return results;
  };

  const copyDriveItems = async (
    itemsToCopy: CopyableItem[],
    destinationDriveId: string,
    destinationItemId: string,
    cloneVersionInsteadOfCopy = false,
    replaceItemOnConflict = false
  ) => {
    const response = await doCopyDriveItems(
      itemsToCopy,
      destinationDriveId,
      destinationItemId,
      cloneVersionInsteadOfCopy,
      replaceItemOnConflict
    );

    for (let i = 0; i < itemsToCopy.length; i++) {
      logActivity(destinationDriveId, response[i].itemId, destinationItemId, "CopyDriveItem", {
        templateId: itemsToCopy[i].parentReference.driveId,
        templateDriveId: itemsToCopy[i].id,
        name: itemsToCopy[i].name
      });
    }
    return response;
  };

  const createNewDriveItemFromTemplate = async (
    templateItems: CopyableItem[],
    destinationDriveId: string,
    destinationItemId: string
  ) => {
    const response = await doCopyDriveItems(templateItems, destinationDriveId, destinationItemId);
    logActivity(destinationDriveId, response[0].itemId, destinationItemId, "CreateDriveItem", {
      templateId: templateItems[0].parentReference.driveId,
      templateDriveId: templateItems[0].id,
      name: templateItems[0].name
    });
    return response;
  };

  const moveDriveItems = async (itemsToMove: CopyableItem[], destinationDriveId: string, destinationItemId: string) => {
    const calcDestinationDriveId = dependencies.calculateDriveIdOrMyDrive.value(destinationDriveId);
    const calculateRootOrItemId = dependencies.getCalculateRootOrItemId();
    const calcDestinationItemId = calculateRootOrItemId(calcDestinationDriveId as string, destinationItemId);

    if (itemsToMove.length > 0) {
      const sourceDriveIdToUpdate = itemsToMove[0].parentReference.driveId;
      const sourceItemIdToUpdate = itemsToMove[0].parentReference.id;

      const progressPromises = itemsToMove.map((item: CopyableItem) => {
        if (item.parentReference.driveId !== calcDestinationDriveId || item.id !== calcDestinationItemId) {
          return apiGraph.patch(`/drives/${item.parentReference.driveId}/items/${item.id}`, {
            parentReference: {
              driveId: calcDestinationDriveId,
              id: calcDestinationItemId
            },
            name: item.newName || item.name
          });
        }
      });

      await Promise.all(progressPromises);

      dependencies.reloadDriveItem(sourceDriveIdToUpdate, sourceItemIdToUpdate);
      dependencies.reloadDriveItem(calcDestinationDriveId as string, calcDestinationItemId);

      itemsToMove.forEach((item: CopyableItem) => {
        logActivity(
          calcDestinationDriveId as string,
          (item as DriveItem & { Id?: string }).Id ?? null,
          calcDestinationItemId,
          "MoveDriveItem",
          {
            itemId: item.id,
            destinationItemId: calcDestinationItemId,
            name: item.name,
            newName: item.newName
          }
        );
      });
    }
  };

  const renameDriveItem = async (driveId: string, itemId: string, parentItemId: string, newDocumentName: string) => {
    let headers: Record<string, string> = { Prefer: "bypass-shared-lock" };
    if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
      headers = {};
    }
    const oldChildren = dependencies.driveItemChildren.value(driveId, parentItemId);
    await apiGraph.patch(`/drives/${driveId}/items/${itemId}`, { name: newDocumentName }, { headers });
    apiSkobo.patch(`/AppDriveItems/${driveId}/${itemId}`, { newDisplayName: newDocumentName }, { headers });
    await dependencies.reloadDriveItem(driveId, parentItemId);
    const oldName = oldChildren.find((child: DriveItem) => child.id === itemId)?.name || "";
    logActivity(driveId, itemId, parentItemId, "Rename", { oldName, newName: newDocumentName });
  };

  const downloadSingleFile = async (driveId: string, itemId: string, requestId: string) => {
    const urlResponse = await graphGet(`/drives/${driveId}/items/${itemId}?select=id,@microsoft.graph.downloadUrl`);

    const configForApiRequest = {
      headers: { Accept: "application/octet-stream" },
      responseType: "blob",
      onDownloadProgress: (progressEvent: { loaded: number; total: number }) => {
        dependencies.downloadProgressState.value[requestId] = Math.floor(
          (progressEvent.loaded / progressEvent.total) * 100
        );
      }
    };

    dependencies.downloadProgressState.value[requestId] = -1;
    const fileResponse = await graphGet(urlResponse["@microsoft.graph.downloadUrl"], configForApiRequest);
    logActivity(driveId, itemId, null, "Download driveItem");
    return fileResponse;
  };

  const downloadMultipleDriveItemsAndFolders = async (itemsToDownload: DriveItem[], requestId: string) => {
    try {
      const parentDriveId = itemsToDownload[0].parentReference.driveId;
      const driveItemIds = itemsToDownload.map((item: DriveItem) => item.id);
      const itemIdArray = driveItemIds;
      const joinedDriveItemIds = itemIdArray.join();

      const configForApiRequest = {
        headers: { Accept: "application/octet-stream" },
        responseType: "blob",
        onDownloadProgress: (progressEvent: { loaded: number; total: number }) => {
          dependencies.downloadProgressState.value[requestId] = Math.floor(
            (progressEvent.loaded / progressEvent.total) * 100
          );
        }
      };

      dependencies.downloadProgressState.value[requestId] = -1;
      const response = await apiSkobo.get(
        `/appdriveitems/zip/drives/${parentDriveId}/?items=${joinedDriveItemIds}`,
        configForApiRequest
      );

      logActivity(parentDriveId, driveItemIds[0], null, "Download multiple driveItems", {
        itemIds: driveItemIds
      });
      return response;
    } catch {
      const ctx = this as
        | { $skboMessage?: { errorMessage?: (m: string) => void }; $t?: (k: string) => string }
        | undefined;
      ctx?.$skboMessage?.errorMessage?.(ctx?.$t?.("Documents.Errors.ErrorDuringDownload") ?? "");
    }
  };

  const uploadDriveItem = async (
    destinationDriveId: string,
    destinationItemId: string,
    fileObject: { name: string; file: File | Blob; size?: number },
    replaceItemOnConflict: boolean
  ) => {
    const session = await createUploadSession(
      destinationDriveId,
      destinationItemId,
      fileObject.name,
      replaceItemOnConflict
    );

    const binary = await FileHelper.getFileAsBinary(fileObject.file);
    const fileChunks = splitFileToChuncks(binary, uploadFileSizeSplitLimit);
    let chunkUploadResponse: { data?: unknown } | undefined;
    let contentRangePositionCounter = 0;

    if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
      for (let i = 0; i < fileChunks.length; i++) {
        const contentRange = `bytes ${contentRangePositionCounter}-${
          contentRangePositionCounter + (fileChunks[i].byteLength - 1)
        }/${binary.byteLength}`;
        await apiGraph.put(session.data.uploadUrl, fileChunks[i], {
          headers: {
            "Content-Range": contentRange,
            "Content-Type": "application/octet-stream"
          }
        });
        contentRangePositionCounter += fileChunks[i].byteLength;
      }

      dependencies.reloadDriveItem(destinationDriveId, destinationItemId);
      logActivity(destinationDriveId, destinationItemId, null, "Upload", { name: fileObject.name });
    } else {
      for (let i = 0; i < fileChunks.length; i++) {
        const contentRange = `bytes ${contentRangePositionCounter}-${
          contentRangePositionCounter + (fileChunks[i].byteLength - 1)
        }/${binary.byteLength}`;
        chunkUploadResponse = await apiGraph.put(session.data.uploadUrl, fileChunks[i], {
          headers: {
            "Content-Range": contentRange,
            "Content-Type": "application/octet-stream"
          }
        });
        contentRangePositionCounter += fileChunks[i].byteLength;
      }

      dependencies.reloadDriveItem(destinationDriveId, destinationItemId);

      logActivity(destinationDriveId, destinationItemId, null, "Upload", {
        name: fileObject.name,
        size: (fileObject.size ?? 0) / 1048576
      });
      return chunkUploadResponse?.data;
    }
  };

  const createFolder = async (driveId: string, parentItemId: string, name: string) => {
    const response = await apiGraph.post(`/drives/${driveId}/items/${parentItemId}/children`, {
      name: name,
      folder: {},
      "@microsoft.graph.conflictBehavior": "rename"
    });
    dependencies.reloadDriveItem(driveId, parentItemId);
    return response;
  };

  const reloadJournalizingAndLockingDocumentsForPersonAndDrive = async (personId: string, driveId: string) => {
    const response = await apiSkobo.get(`/appconfidentialfolder/journalizedFiles/user/${personId}`);
    if (!journalizingAndLockingDocumentsPerPersonAndDriveMap.value[personId]) {
      journalizingAndLockingDocumentsPerPersonAndDriveMap.value[personId] = {};
    }
    journalizingAndLockingDocumentsPerPersonAndDriveMap.value[personId][driveId] = (
      response.data as JournalLockEntry[]
    ).filter((doc: JournalLockEntry) => doc.status !== "finished");
  };

  const loadJournalizingAndLockingDocumentsForPersonAndDrive = async (personId: string, driveId: string) => {
    if (
      !journalizingAndLockingDocumentsPerPersonAndDriveMap.value[personId] ||
      !journalizingAndLockingDocumentsPerPersonAndDriveMap.value[personId][driveId]
    ) {
      return await reloadJournalizingAndLockingDocumentsForPersonAndDrive(personId, driveId);
    }
  };

  const reloadJournalizingAndLockingDocumentsForSectionAndDrive = async (sectionId: string, driveId: string) => {
    const response = await apiSkobo.get(`/appconfidentialfolder/journalizedFiles/user/${sectionId}`);
    if (!journalizingAndLockingDocumentsPerSectionAndDriveMap.value[sectionId]) {
      journalizingAndLockingDocumentsPerSectionAndDriveMap.value[sectionId] = {};
    }
    journalizingAndLockingDocumentsPerSectionAndDriveMap.value[sectionId][driveId] = (
      response.data as JournalLockEntry[]
    ).filter((doc: JournalLockEntry) => doc.status !== "finished");
  };

  const loadJournalizingAndLockingDocumentsForSectionAndDrive = async (sectionId: string, driveId: string) => {
    if (
      !journalizingAndLockingDocumentsPerSectionAndDriveMap.value[sectionId] ||
      !journalizingAndLockingDocumentsPerSectionAndDriveMap.value[sectionId][driveId]
    ) {
      return await reloadJournalizingAndLockingDocumentsForSectionAndDrive(sectionId, driveId);
    }
  };

  const reloadConfidentialDocumentsSharedToAulaForUserOnDrive = async (userId: string, driveId: string) => {
    const response = await apiSkobo.get(`/appconfidentialfolder/sharetorelations/${userId}/${driveId}`);
    if (!confidentialDocumentsSharedToAulaForUserDriveMap.value[userId]) {
      confidentialDocumentsSharedToAulaForUserDriveMap.value[userId] = {};
    }
    confidentialDocumentsSharedToAulaForUserDriveMap.value[userId][driveId] = response.data;
  };

  const loadConfidentialDocumentsSharedToAulaForUserOnDrive = async (userId: string, driveId: string) => {
    if (
      !confidentialDocumentsSharedToAulaForUserDriveMap.value[userId] ||
      !confidentialDocumentsSharedToAulaForUserDriveMap.value[userId][driveId]
    ) {
      await reloadConfidentialDocumentsSharedToAulaForUserOnDrive(userId, driveId);
    }
  };

  const addDocumentSharedToRelation = async (
    userId: string,
    driveId: string,
    itemId: string,
    expireDateTime: string,
    fileName: string
  ) => {
    await apiSkobo.post(`/appconfidentialfolder/sharetorelations`, {
      userId,
      driveId,
      itemId,
      expireDateTime,
      fileName
    });
    await reloadConfidentialDocumentsSharedToAulaForUserOnDrive(userId, driveId);
    logActivity(driveId, itemId, null, "AddShareToAula", { fileName, userId, expireDateTime });
  };

  const deleteDocumentSharedToRelation = async (userId: string, driveId: string, itemId: string) => {
    await apiSkobo.delete(`/appconfidentialfolder/sharetorelations/${userId}/${driveId}/${itemId}`);
    await reloadConfidentialDocumentsSharedToAulaForUserOnDrive(userId, driveId);
    logActivity(driveId, itemId, null, "DeleteShareToAula", { userId });
  };

  const reloadJournalizedAndLockedDocuments = async (appConfId: string) => {
    const response = await apiSkobo.get(`/appconfidentialfolder/${appConfId}/itemstatus`);
    journalizedAndLockedDocuments.value[appConfId] = response.data;
  };

  const loadJournalizedAndLockedDocuments = async (appConfId: string) => {
    if (!journalizedAndLockedDocuments.value[appConfId]) {
      await reloadJournalizedAndLockedDocuments(appConfId);
    }
    return journalizedAndLockedDocuments.value[appConfId];
  };

  const lockDocuments = async (appConfId: string, itemsToLock: DriveItem[]) => {
    const featuresStore = useFeaturesStore();
    if (!featuresStore.isFeatureEnabled("DocumentConfidentialLock")) {
      return;
    }
    try {
      const initLockPromises = itemsToLock.map((item: DriveItem) =>
        apiSkobo.post(`/appconfidentialfolder/${appConfId}/lock`, {
          driveId: item.parentReference.driveId,
          itemId: item.id,
          fileName: item.name
        })
      );
      const initLockResults = await Promise.allSettled(initLockPromises);

      type RejectedReason = { response?: { data?: string | { error?: string } } };
      const awaitLockPromises = initLockResults.map(async (promiseResult: PromiseSettledResult<unknown>) => {
        if (promiseResult.status === "rejected") {
          const globalsStore = useGlobalsStore();
          const reason = promiseResult.reason as RejectedReason;
          const data = reason?.response?.data;
          if (typeof data === "string" && data.includes("is locked for shared use")) {
            globalsStore.setErrorMessage("$t('Documents.Errors.LockDocumentToDocumentIsLockedBySomeoneElse')");
          } else if (typeof data === "object" && data?.error?.startsWith("This file has already been locked")) {
            globalsStore.setErrorMessage("$t('Errors.Lock.AlreadyLocked')");
          } else {
            globalsStore.setErrorMessage("$t('Errors.Lock.AnErrorOccurred')");
          }
        } else {
          return;
        }
      });

      await Promise.all(awaitLockPromises);
      await loadJournalizedAndLockedDocuments(appConfId);

      const parent = itemsToLock[0].parentReference;
      const reload = dependencies.getReload();
      reload(parent.driveId, parent.id!);

      itemsToLock.forEach((item: DriveItem) => {
        dependencies.reloadDocumentPermissions(item.parentReference.driveId, item.id);
        logActivity(item.parentReference.driveId, item.id, null, "Lock document");
      });
    } catch {
      const globalsStore = useGlobalsStore();
      globalsStore.setErrorMessage("$t('Errors.Lock.AnErrorOccurred')");
      throw new Error();
    }
  };

  const journalizeUserDocuments = async (userId: string, itemsToJournalize: DriveItem[]) => {
    const peopleStore = usePeopleStore();
    const student = peopleStore.fromId(userId) as { name?: { name?: string } } | null;
    try {
      const initJournalizingPromises = itemsToJournalize.map(async (item: DriveItem) =>
        apiSkobo.post(`/appconfidentialfolder/journalize/user/${userId}`, {
          driveId: item.parentReference.driveId,
          itemId: item.id,
          fileName: item.name,
          foldername: student?.name?.name ?? ""
        })
      );
      await Promise.all(initJournalizingPromises);
    } catch (caughtError: unknown) {
      const globalsStore = useGlobalsStore();
      if ((caughtError as { status?: number })?.status === 423) {
        globalsStore.setErrorMessage("$t('Documents.Errors.JournalizeDocumentToDocumentIsLockedBySomeoneElse')");
      } else {
        globalsStore.setErrorMessage("$t('Documents.Errors.ErrorDuringJournalizing')");
      }
    }
  };

  const journalizeSectionDocuments = async (sectionId: string, itemsToJournalize: DriveItem[]) => {
    const initJournalizingPromises = itemsToJournalize.map(async (item: DriveItem) =>
      apiSkobo.post(`/appconfidentialfolder/journalize/section/${sectionId}`, {
        driveId: item.parentReference.driveId,
        itemId: item.id,
        fileName: item.name
      })
    );
    await Promise.allSettled(initJournalizingPromises);
  };

  const journalizeInstitutionDocuments = async (itemsToJournalize: DriveItem[]) => {
    const institutionsStore = useInstitutionsStore();
    const initJournalizingPromises = itemsToJournalize.map(async (item: DriveItem) =>
      apiSkobo.post(`/appconfidentialfolder/journalize/institution/${institutionsStore.myActiveInstitutionId}`, {
        driveId: item.parentReference.driveId,
        itemId: item.id,
        fileName: item.name
      })
    );
    await Promise.allSettled(initJournalizingPromises);
  };

  const hideDriveItems = async (itemsToHide: DriveItem[]) => {
    await itemsToHide.map(async (item: DriveItem) => {
      const driveId = DriveItemHelper.getDriveId(item);
      const itemId = item.id;

      const meStore = useMeStore();
      const userId = meStore.userId;
      let existingHidden = new Array(...hiddenSharedFilesState.value);
      if (!existingHidden) {
        existingHidden = [];
      }
      existingHidden.push({ driveId, itemId });

      await axios.post(
        `https://skobostats.azurewebsites.net/api/UserSettings?code=TCsYhdXzGIodM/bwslBH/eCvoW4vO0He3HHzwBkmlrSk1r29clZJzQ==`,
        { key: "hiddenSharedFilesState", value: JSON.stringify(existingHidden), userId }
      );

      const listInState = hiddenSharedFilesState.value;
      listInState.push({ driveId, itemId });
      hiddenSharedFilesState.value = listInState;
    });

    const parentDriveId = DriveItemHelper.getDriveId(itemsToHide[0]);
    const parentItemId = itemsToHide[0].remoteItem
      ? itemsToHide[0].remoteItem.parentReference.id
      : itemsToHide[0].parentReference.id;
    dependencies.reloadDriveItem(parentDriveId, parentItemId);
  };

  const loadHiddenDriveItems = async () => {
    const meStore = useMeStore();
    const userId = meStore.userId;

    const response = await axios.get(
      `https://skobostats.azurewebsites.net/api/UserSettings?code=TCsYhdXzGIodM/bwslBH/eCvoW4vO0He3HHzwBkmlrSk1r29clZJzQ==&u=${userId}`
    );
    hiddenSharedFilesState.value =
      JSON.parse(
        (response.data as Array<{ Key: string; Value: string }>).find(s => s.Key === "hiddenSharedFilesState")?.Value ??
          "[]"
      ) || [];
  };

  return {
    journalizedAndLockedDocuments,
    confidentialDocumentsSharedToAulaForUserDriveMap,
    confidentialDocumentsJournalizedPerUserMap,
    confidentialDocumentsJournalizedPerSectionMap,
    hiddenSharedFilesState,
    journalizingAndLockingDocumentsPerPersonAndDriveMap,
    journalizingAndLockingDocumentsPerSectionAndDriveMap,
    appConfidentialId,
    isDocumentJournalizing,
    isDocumentLocking,
    confidentialDocumentsSharedToAulaForUserIdOnDriveId,
    isConfidentialDocumentShareToAulaForUserIdOnDriveId,
    journalizedConfidentialDocumentsForUserOnDrive,
    journalizedConfidentialDocumentsForSectionOnDrive,
    hiddenSharedFiles,
    drivesICanCopyItemsTo,
    createNewDriveItemFromTemplate,
    copyDriveItems,
    doCopyDriveItems,
    moveDriveItems,
    renameDriveItem,
    downloadSingleFile,
    downloadMultipleDriveItemsAndFolders,
    deleteDriveItems,
    uploadDriveItem,
    createFolder,
    loadJournalizingAndLockingDocumentsForPersonAndDrive,
    reloadJournalizingAndLockingDocumentsForPersonAndDrive,
    loadJournalizingAndLockingDocumentsForSectionAndDrive,
    reloadJournalizingAndLockingDocumentsForSectionAndDrive,
    addDocumentSharedToRelation,
    deleteDocumentSharedToRelation,
    loadConfidentialDocumentsSharedToAulaForUserOnDrive,
    reloadConfidentialDocumentsSharedToAulaForUserOnDrive,
    lockDocuments,
    journalizeUserDocuments,
    journalizeSectionDocuments,
    journalizeInstitutionDocuments,
    loadJournalizedAndLockedDocuments,
    reloadJournalizedAndLockedDocuments,
    hideDriveItems,
    loadHiddenDriveItems,
    logActivity
  };
};
